import { CheckCircle2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../../../Redux/hooks";
import { cancelOrder } from "../../../../Redux/custmoer_orders/slice";
import "./FoodOrderCancelDialog.css";

type FoodOrderCancelDialogProps = {
  open: boolean;
  orderId: string | null;
  orderLabel?: string;
  onClose: () => void;
  onCancelled?: () => void;
};

const CANCEL_REASONS = [
  "Changed my mind",
  "Found a better price",
  "Delivery is too slow",
  "Ordered by mistake",
  "Other",
];

const FoodOrderCancelDialog = ({
  open,
  orderId,
  orderLabel,
  onClose,
  onCancelled,
}: FoodOrderCancelDialogProps) => {
  const dispatch = useAppDispatch();
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) return;

    setReason("");
    setSubmitting(false);
    setDone(false);

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [open, onClose]);

  if (!open) return null;

  const handleConfirm = async () => {
    if (!orderId) return;
    if (!reason.trim()) {
      toast.error("Please provide a reason");
      return;
    }

    setSubmitting(true);
    try {
      await dispatch(cancelOrder({ orderId, reason })).unwrap();
      setDone(true);
      onCancelled?.();
    } catch (err: any) {
      toast.error(err || "Failed to cancel order");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="food-cancel-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !submitting) {
          onClose();
        }
      }}
    >
      <section
        className="food-cancel-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="food-cancel-title"
      >
        <button
          type="button"
          className="food-cancel-close"
          onClick={onClose}
          disabled={submitting}
          aria-label="Close"
        >
          <X size={18} strokeWidth={2} />
        </button>

        {done ? (
          <>
            <span className="food-cancel-success-icon">
              <CheckCircle2 size={34} strokeWidth={2} />
            </span>
            <h2 id="food-cancel-title">Order Cancelled</h2>
            <p>
              Order #{orderLabel || orderId} has been cancelled successfully.
            </p>
            <button
              type="button"
              className="food-cancel-done"
              onClick={onClose}
            >
              Done
            </button>
          </>
        ) : (
          <>
            <h2 id="food-cancel-title">Cancel Order</h2>
            <p>
              Are you sure you want to cancel order #{orderLabel || orderId}?
              This action cannot be undone.
            </p>
            <select
              className="food-cancel-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
            >
              <option value="">Select a reason</option>
              {CANCEL_REASONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <div className="food-cancel-actions">
              <button
                type="button"
                className="food-cancel-keep"
                onClick={onClose}
                disabled={submitting}
              >
                Keep Order
              </button>
              <button
                type="button"
                className="food-cancel-confirm"
                onClick={handleConfirm}
                disabled={submitting || !reason}
              >
                {submitting ? "Cancelling..." : "Confirm Cancel"}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default FoodOrderCancelDialog;
